import type { StrategyRdMatch, StrategyRdMatchMeta } from "../types/strategyRd";
import {
  executionCandleTypeDurationMs,
  executionCandleTypesMatch,
  formatExecutionCandleTypeLabel,
  projectExecutionBarWindowOntoChartTf,
} from "./executionCandleTimeframe";
import {
  strategyRdConceptChartLabel,
  strategyRdHtfBiasChartLabel,
} from "./strategyRdConceptCopy";
import { strategyRdConceptUsesChartIndicator } from "./strategyRdConceptIndicators";
import {
  extractStrategyRdCoilSetupSpan,
  extractStrategyRdMetaZone,
  extractStrategyRdBreakerSetupZone,
  extractStrategyRdOrderBlockSetupZone,
  extractStrategyRdStageSetupGeometry,
  extractStrategyRdStageSegments,
  strategyRdIsBreakerSetupId,
  strategyRdIsLevelPairSetupId,
  strategyRdIsOrderBlockSetupId,
  strategyRdIsStageSetupId,
  strategyRdStageDrawMarkers,
} from "./strategyRdConcepts";
import { strategyRdPathSetupZone, strategyRdPathReadyEnter, strategyRdMatchDisplayDirection } from "./strategyRdPath";
import {
  strategyRdWaveCIsBandId,
  strategyRdWaveCIsCandlePatternId,
  strategyRdWaveCIsChartPatternId,
  strategyRdWaveCIsDonchianId,
  strategyRdWaveCIsKillzoneId,
  strategyRdWaveCIsOscillatorId,
  strategyRdWaveCIsRsId,
  strategyRdWaveCIsVolumeId,
  strategyRdWaveCHasVolumeEvidence,
  parseStrategyRdWaveCLocation,
} from "./strategyRdWaveCLocation";
import { strategyRdWaveCDrawingsFromMeta } from "./strategyRdWaveCDrawings";
import type { StrategyRdSessionOption } from "../types/strategyRd";

export type StrategyRdConceptDrawingKind = "bar" | "zone" | "level" | "marker" | "ray";

/** One labeled concept overlay around the example match location. */
export type StrategyRdConceptDrawing = {
  id: string;
  label: string;
  kind: StrategyRdConceptDrawingKind;
  barTime: string | null;
  /** Inclusive left edge of a zone (FVG spans candle 1 → 3). For rays: start time. */
  fromBarTime?: string | null;
  /** Inclusive right edge of a zone. For rays: end time. */
  toBarTime?: string | null;
  high: number | null;
  low: number | null;
  price: number | null;
  /** Ray start price (`y1`). When set with `toPrice`, preferred over high/low. */
  fromPrice?: number | null;
  /** Ray end price (`y2`). */
  toPrice?: number | null;
  /** Ray/segment extend policy. Default at emit: `right` (channels); H&S silhouette uses `none`. */
  lineExtend?: "none" | "right";
  /** Alchemy divergence price_line arrow tip. */
  arrowRight?: boolean;
  /** When false, keep the box/line but skip the pill (preview overview). */
  showLabel?: boolean;
  /** Point mark. Enter uses an arrow at the fill price instead of a text pill. */
  markerStyle?: "arrowup" | "arrowdown" | "diamond";
  /** Source TF when projected onto a different chart TF. */
  sourceCandleType?: string | null;
  /** Optional Vela pane id (e.g. tessera-panes / tessera-macd / volume). */
  paneId?: string | null;
  /**
   * Lab chart group (one legend entry per recipe chip): `setup`, `entry`, or `conf-N`
   * (N = the confluence chip's position in the composer).
   */
  group?: string | null;
};

export type StrategyRdConceptConfluenceRef = {
  id: string;
  candle_type?: string | null;
};

export type StrategyRdConceptOhlcLike = {
  timestamp?: string | null;
  time?: string | null;
  open?: number | null;
  high: number | null;
  low: number | null;
  close?: number | null;
};

function candleTime(c: StrategyRdConceptOhlcLike): string | null {
  const t = (c.timestamp || c.time || "").trim();
  return t || null;
}

/** OHLC of chart-TF bars that fall inside an HTF wall-clock window. */
export function aggregateConceptOhlcWindow(
  candles: readonly StrategyRdConceptOhlcLike[] | null | undefined,
  fromBarTime: string | null | undefined,
  toBarTime: string | null | undefined
): { open: number; high: number; low: number; close: number } | null {
  const list = candles ?? [];
  const fromMs = Date.parse((fromBarTime || "").trim().replace(/\+00:00$/, "Z"));
  const toMs = Date.parse((toBarTime || "").trim().replace(/\+00:00$/, "Z"));
  if (!list.length || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  const loBound = Math.min(fromMs, toMs);
  const hiBound = Math.max(fromMs, toMs);
  let open: number | null = null;
  let close: number | null = null;
  let high = Number.NEGATIVE_INFINITY;
  let low = Number.POSITIVE_INFINITY;
  for (const c of list) {
    const t = candleTime(c);
    if (!t) continue;
    const ms = Date.parse(t.replace(/\+00:00$/, "Z"));
    if (!Number.isFinite(ms) || ms < loBound || ms > hiBound) continue;
    const h = Number(c.high);
    const l = Number(c.low);
    const o = Number(c.open);
    const cl = Number(c.close);
    if (Number.isFinite(h)) high = Math.max(high, h);
    if (Number.isFinite(l)) low = Math.min(low, l);
    if (open == null && Number.isFinite(o)) open = o;
    if (Number.isFinite(cl)) close = cl;
  }
  if (!(high > low) || !Number.isFinite(high) || !Number.isFinite(low)) return null;
  return {
    open: open ?? low,
    high,
    low,
    close: close ?? high,
  };
}

function asConfluenceRefs(
  confluences: readonly (string | StrategyRdConceptConfluenceRef)[] | null | undefined
): StrategyRdConceptConfluenceRef[] {
  if (!confluences?.length) return [];
  const out: StrategyRdConceptConfluenceRef[] = [];
  for (const item of confluences) {
    if (typeof item === "string") {
      const id = item.trim();
      if (id) out.push({ id });
      continue;
    }
    const id = (item?.id || "").trim();
    if (!id) continue;
    out.push({
      id,
      candle_type: item.candle_type != null ? String(item.candle_type) : null,
    });
  }
  return out;
}

function withProjectedTf(
  drawing: StrategyRdConceptDrawing,
  sourceTf: string | null | undefined,
  chartTf: string | null | undefined
): StrategyRdConceptDrawing {
  const src = (sourceTf || "").trim();
  const chart = (chartTf || "").trim();
  if (!src || !chart || executionCandleTypesMatch(src, chart)) {
    return drawing;
  }
  const projected = projectExecutionBarWindowOntoChartTf({
    barTime: drawing.barTime,
    fromBarTime: drawing.fromBarTime,
    toBarTime: drawing.toBarTime,
    sourceCandleType: src,
    chartCandleType: chart,
  });
  const tfLabel = formatExecutionCandleTypeLabel(src) || src;
  const baseLabel = (drawing.label || "").trim();
  const label =
    baseLabel && !baseLabel.includes(tfLabel) ? `${baseLabel} · ${tfLabel}` : baseLabel || tfLabel;

  // Project times only. Never invent high/low from chart OHLC — boxes come from BE meta.
  return {
    ...drawing,
    label,
    barTime: projected.barTime,
    fromBarTime: projected.fromBarTime,
    toBarTime: projected.toBarTime,
    sourceCandleType: src,
  };
}

function finiteNum(value: unknown): number | null {
  // Number(null) === 0 — never coerce null/undefined/"" into a fake price.
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function asTime(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

function barTimeMs(value: string | null | undefined): number | null {
  const t = (value || "").trim().replace(/\+00:00$/, "Z");
  if (!t) return null;
  const ms = Date.parse(t);
  return Number.isFinite(ms) ? ms : null;
}

/** Hide location zones that would arm after the path fill. */
function locationArmsAfterEntry(
  armBarTime: string | null | undefined,
  entryBarTime: string | null | undefined
): boolean {
  const armMs = barTimeMs(armBarTime);
  const entryMs = barTimeMs(entryBarTime);
  if (armMs == null || entryMs == null) return false;
  return armMs > entryMs;
}

export type StrategyRdConfluenceEvidenceEntry = {
  /** BE slot key (`near_ob`, `near_ob#2`, `above_sma50`). */
  slotKey: string;
  /** Concept that produced the evidence (`evidence.concept_id`, never parsed from the key). */
  conceptId: string;
  evidence: Record<string, unknown>;
  /** Composer chip the evidence belongs to (TF fallback). */
  ref: StrategyRdConceptConfluenceRef | null;
  /** That chip's position in the composer (chart group `conf-N`). */
  chipIndex: number;
};

/** A family chip (`rsi`, `sma`, `regime`) resolves to concrete ids (`rsi_oversold`, `above_sma50`). */
function confluenceRefMatchesConcept(refId: string, conceptId: string): boolean {
  const ref = refId.trim().toLowerCase();
  const concept = conceptId.trim().toLowerCase();
  if (!ref || !concept) return false;
  if (ref === concept) return true;
  return new RegExp(`(^|_)${ref.replace(/[^a-z0-9_]/g, "")}`).test(concept);
}

/**
 * Confluence evidence to draw, from `meta.confluences` keyed by slot key
 * (`near_ob`, `near_ob#2`). The concept comes from `evidence.concept_id`.
 * Only keys present in `meta.confluences` are drawn — never a chip from the recipe with no
 * evidence, and never `meta.confluences_not_applied` (slots whose Trade side excludes this
 * match; they were not checked). For neutral setups `meta.confluences` is already the traded
 * side (`meta.path_side`).
 */
export function strategyRdConfluenceEvidenceEntries(
  meta: StrategyRdMatchMeta | null | undefined,
  confluences: readonly (string | StrategyRdConceptConfluenceRef)[] | null | undefined
): StrategyRdConfluenceEvidenceEntry[] {
  const refs = asConfluenceRefs(confluences);
  if (refs.length === 0) return [];
  const bucket = asRecord(meta?.confluences) ?? {};
  const notApplied = new Set(
    (Array.isArray(meta?.confluences_not_applied) ? meta!.confluences_not_applied : [])
      .map((k) => String(k).trim())
      .filter(Boolean)
  );
  const out: Array<Omit<StrategyRdConfluenceEvidenceEntry, "chipIndex"> & { refIndex: number }> = [];
  const usedRefs = new Set<number>();
  for (const [rawKey, rawRow] of Object.entries(bucket)) {
    const row = asRecord(rawRow);
    const slotKey = rawKey.trim();
    if (!row || !slotKey || notApplied.has(slotKey)) continue;
    const conceptId =
      (typeof row.concept_id === "string" && row.concept_id.trim()) ||
      slotKey.split("#", 1)[0]!.trim();
    if (!conceptId) continue;
    const exact = (ref: StrategyRdConceptConfluenceRef) =>
      ref.id.trim().toLowerCase() === conceptId.toLowerCase();
    const family = (ref: StrategyRdConceptConfluenceRef) =>
      confluenceRefMatchesConcept(ref.id, conceptId);
    // Unused exact chip, then unused family chip; one chip can also resolve to
    // several BE slots (MA / RSI with Location Both), so fall back to a used one.
    const refIndex = [
      refs.findIndex((ref, i) => !usedRefs.has(i) && exact(ref)),
      refs.findIndex((ref, i) => !usedRefs.has(i) && family(ref)),
      refs.findIndex(exact),
      refs.findIndex(family),
    ].find((i) => i >= 0);
    // Evidence for a concept no chip asked for (stale / other recipe) — skip.
    if (refIndex == null) continue;
    usedRefs.add(refIndex);
    out.push({ slotKey, conceptId, evidence: row, ref: refs[refIndex]!, refIndex });
  }
  // Chip order (stable within a chip) so pills stack the same way as the composer.
  return out
    .sort((a, b) => a.refIndex - b.refIndex)
    .map(({ refIndex, ...entry }) => ({ ...entry, chipIndex: refIndex }));
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function strategyRdConceptDrawingKindForId(
  id: string | null | undefined
): StrategyRdConceptDrawingKind {
  const key = (id || "").trim().toLowerCase();
  if (!key) return "marker";
  // Areas / bands → rectangle. Highs, lows, swings → horizontal level.
  if (
    key.includes("fvg") ||
    key.includes("order_block") ||
    key.includes("breaker") ||
    /(^|_)ob(_|$)/.test(key) ||
    key.includes("session_first") ||
    key.includes("opening_range") ||
    key.includes("coil")
  ) {
    return "zone";
  }
  if (
    strategyRdIsLevelPairSetupId(key) ||
    key.includes("pdh") ||
    key.includes("pdl") ||
    key.includes("previous_day") ||
    key.includes("swing") ||
    key === "near_prior_sr" ||
    key.includes("liq") ||
    key.includes("sweep") ||
    key.includes("run")
  ) {
    return "level";
  }
  if (key.includes("engulf") || key.includes("inside_bar")) return "bar";
  if (key.includes("sma") || key.endsWith("_level") || key.includes("limit_at_level")) {
    return "level";
  }
  return "marker";
}

type ConfluenceLocation = {
  barTime: string | null;
  fromBarTime: string | null;
  toBarTime: string | null;
  high: number | null;
  low: number | null;
  price: number | null;
  candleType: string | null;
};

function emptyConfluenceLocation(): ConfluenceLocation {
  return {
    barTime: null,
    fromBarTime: null,
    toBarTime: null,
    high: null,
    low: null,
    price: null,
    candleType: null,
  };
}

/** Drawable coords from one BE evidence row only — never invent geometry. */
function confluenceLocationFromRow(row: Record<string, unknown> | null): ConfluenceLocation {
  if (!row) return emptyConfluenceLocation();

  const high =
    finiteNum(row.zone_high) ??
    finiteNum(row.fvg_high) ??
    finiteNum(row.ob_high) ??
    finiteNum(row.breaker_high) ??
    finiteNum(row.swing_high) ??
    finiteNum(row.high) ??
    finiteNum(row.level_high);
  const low =
    finiteNum(row.zone_low) ??
    finiteNum(row.fvg_low) ??
    finiteNum(row.ob_low) ??
    finiteNum(row.breaker_low) ??
    finiteNum(row.swing_low) ??
    finiteNum(row.low) ??
    finiteNum(row.level_low);
  const zoneOk = high != null && low != null && high > low;
  const price =
    finiteNum(row.ma_value) ??
    finiteNum(row.value) ??
    finiteNum(row.price) ??
    finiteNum(row.level) ??
    finiteNum(row.fvg_ce) ??
    finiteNum(row.close) ??
    (zoneOk ? (high! + low!) / 2 : null);
  const barTime = asTime(row.bar_time) ?? asTime(row.timestamp) ?? asTime(row.time);
  const fromBarTime =
    asTime(row.from_bar_time) ??
    asTime(row.origin_bar_time) ??
    asTime(row.ob_bar_time) ??
    asTime(row.origin_ob_bar_time) ??
    barTime;
  const toBarTime =
    asTime(row.to_bar_time) ??
    asTime(row.impulse_bar_time) ??
    asTime(row.mss_bar_time) ??
    asTime(row.fail_bar_time) ??
    barTime;
  const candleType =
    typeof row.candle_type === "string" && row.candle_type.trim()
      ? row.candle_type.trim()
      : null;

  return {
    barTime,
    fromBarTime: zoneOk || fromBarTime ? fromBarTime : null,
    toBarTime: zoneOk || toBarTime ? toBarTime : null,
    high: zoneOk ? high : null,
    low: zoneOk ? low : null,
    price,
    candleType,
  };
}

function kindFromConfluenceLocation(
  id: string,
  loc: ConfluenceLocation
): StrategyRdConceptDrawingKind {
  if (loc.high != null && loc.low != null && loc.high > loc.low) return "zone";
  if (loc.price != null) return "level";
  return strategyRdConceptDrawingKindForId(id) === "bar" ? "bar" : "marker";
}

function sameBarTime(a: string, b: string): boolean {
  const left = a.trim().replace(/\+00:00$/, "Z");
  const right = b.trim().replace(/\+00:00$/, "Z");
  if (left === right || left.replace(/Z$/, "") === right.replace(/Z$/, "")) return true;
  const la = Date.parse(left);
  const rb = Date.parse(right);
  return Number.isFinite(la) && Number.isFinite(rb) && la === rb;
}

/**
 * Stage charts pile Prior/Current + Breakout + Enter + SMA + bare Stage N on one bar.
 * Keep the story pills; mute redundant signal-bar tags (SMA line / bare Stage N /
 * Break level title when Base marker exists).
 */
function declutterStageConceptLabels(
  drawings: StrategyRdConceptDrawing[],
  opts: { signalBarTime: string | null; enterBarTime: string | null }
): void {
  const hasStoryLabel = drawings.some((d) => {
    if (!d.id.includes("setup-stage-seg:")) return false;
    const t = d.label.trim().toLowerCase();
    return t.startsWith("prior") || t.startsWith("current");
  });
  const hasBaseMarker = drawings.some(
    (d) => d.id.includes("setup-marker:") && d.id.includes(":base:")
  );
  const onSignal = (d: StrategyRdConceptDrawing) => {
    const t = d.barTime || d.toBarTime;
    if (!t) return false;
    if (opts.signalBarTime && sameBarTime(t, opts.signalBarTime)) return true;
    if (opts.enterBarTime && sameBarTime(t, opts.enterBarTime)) return true;
    return false;
  };

  for (const d of drawings) {
    if (d.id.includes("setup-ma:")) {
      d.showLabel = false;
      continue;
    }
    if (d.id.includes("setup-level:") && hasBaseMarker) {
      // Horizontal rail stays; Base/Breakout markers carry the words.
      d.showLabel = false;
      continue;
    }
    if (
      hasStoryLabel &&
      d.id.includes("setup-stage-seg:") &&
      /^stage\s*[1-4]\s*$/i.test(d.label.trim())
    ) {
      d.showLabel = false;
      continue;
    }
    // Bare Stage N marker left on the signal bar when Current already says Stage N.
    if (
      hasStoryLabel &&
      onSignal(d) &&
      /^stage\s*[1-4]\s*$/i.test(d.label.trim())
    ) {
      d.showLabel = false;
    }
  }
}

/** Latest ISO bar among candidates (FE extend zones toward path story). */
function latestBarTime(...times: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const raw of times) {
    const t = (raw || "").trim();
    if (!t) continue;
    const ms = Date.parse(t.replace(/\+00:00$/, "Z"));
    if (!Number.isFinite(ms)) continue;
    if (ms >= bestMs) {
      bestMs = ms;
      best = t;
    }
  }
  return best;
}

function indexOfBarTime(
  candles: readonly StrategyRdConceptOhlcLike[],
  barTime: string
): number {
  const want = barTime.trim();
  if (!want) return -1;
  for (let i = 0; i < candles.length; i += 1) {
    const got = candleTime(candles[i]!);
    if (got && sameBarTime(got, want)) return i;
  }
  return -1;
}

function isFvgSetupId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  return key.includes("fvg") || key.includes("fair_value");
}

/**
 * Same-timeframe moving averages paint as a continuous line.
 * Stage bands and clear-of-opposing S/R are drawn from confluence evidence.
 */
export function strategyRdConceptIsChartWideFilter(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return true;
  return strategyRdConceptUsesChartIndicator(key);
}

function chartDrawBool(
  evidence: Record<string, unknown> | null,
  key: string,
  fallback: boolean
): boolean {
  const draw = asRecord(evidence?.chart_draw);
  if (!draw || typeof draw[key] !== "boolean") return fallback;
  return draw[key] === true;
}

/** Stage confluence: segment bands + SMA. No base box and no break level. */
function pushStageConfluenceDrawings(
  out: StrategyRdConceptDrawing[],
  opts: {
    id: string;
    evidence: Record<string, unknown> | null;
    sourceTf: string;
    chartTf: string;
    fallbackBar: string | null;
  }
): void {
  const { id, evidence, sourceTf, chartTf, fallbackBar } = opts;
  const segments = extractStrategyRdStageSegments(evidence as StrategyRdMatchMeta | null);
  const maValue = finiteNum(evidence?.ma_value) ?? finiteNum(evidence?.sma_value);
  const maLength = finiteNum(evidence?.ma_length);
  const barTime =
    asTime(evidence?.bar_time) ??
    segments[segments.length - 1]?.toBarTime ??
    fallbackBar;
  const showBandLabel = chartDrawBool(evidence, "stage_label_on_band", true);
  const showSmaBadge = chartDrawBool(evidence, "sma_badge_on_ma", true);
  for (const seg of segments) {
    const hasFill = seg.high != null && seg.low != null && seg.high > seg.low;
    out.push(
      withProjectedTf(
        {
          id: `confluence:${id}:stage-seg:${id}:${seg.stage ?? "x"}:${seg.fromBarTime}`,
          label: seg.label,
          kind: hasFill ? "zone" : "marker",
          barTime: seg.toBarTime || seg.fromBarTime,
          fromBarTime: seg.fromBarTime,
          toBarTime: seg.toBarTime,
          high: hasFill ? seg.high : null,
          low: hasFill ? seg.low : null,
          price: hasFill ? (seg.high! + seg.low!) / 2 : null,
          showLabel: showBandLabel,
        },
        sourceTf,
        chartTf
      )
    );
  }
  if (maValue != null && barTime) {
    const len =
      maLength != null && Number.isFinite(maLength) ? Math.round(maLength) : null;
    out.push(
      withProjectedTf(
        {
          id: `confluence:${id}:ma`,
          label: len != null ? `SMA ${len}` : "SMA",
          kind: "marker",
          barTime,
          fromBarTime: null,
          toBarTime: null,
          high: null,
          low: null,
          price: maValue,
          showLabel: showSmaBadge,
        },
        sourceTf,
        chartTf
      )
    );
  }
}

/** Prior-period high and low for clear-of-opposing S/R. */
function pushClearOfOpposingSrDrawings(
  out: StrategyRdConceptDrawing[],
  opts: {
    id: string;
    evidence: Record<string, unknown> | null;
    sourceTf: string;
    chartTf: string;
    fallbackBar: string | null;
  }
): boolean {
  const { id, evidence, sourceTf, chartTf, fallbackBar } = opts;
  const high = finiteNum(evidence?.level_high) ?? finiteNum(evidence?.high);
  const low = finiteNum(evidence?.level_low) ?? finiteNum(evidence?.low);
  if (high == null && low == null) return false;
  const fromBarTime = asTime(evidence?.from_bar_time);
  const toBarTime = asTime(evidence?.to_bar_time);
  const barTime = asTime(evidence?.bar_time) ?? toBarTime ?? fromBarTime ?? fallbackBar;
  const highLabel =
    typeof evidence?.high_label === "string" && evidence.high_label.trim()
      ? evidence.high_label.trim()
      : "High";
  const lowLabel =
    typeof evidence?.low_label === "string" && evidence.low_label.trim()
      ? evidence.low_label.trim()
      : "Low";
  const rails: Array<{ side: "high" | "low"; price: number; label: string }> = [];
  if (high != null) rails.push({ side: "high", price: high, label: highLabel });
  if (low != null && low !== high) rails.push({ side: "low", price: low, label: lowLabel });
  for (const rail of rails) {
    out.push(
      withProjectedTf(
        {
          id: `confluence:${id}:${rail.side}`,
          label: rail.label,
          kind: "level",
          barTime,
          fromBarTime,
          toBarTime,
          high: null,
          low: null,
          price: rail.price,
          showLabel: true,
        },
        sourceTf,
        chartTf
      )
    );
  }
  return rails.length > 0;
}

type SweepHit = {
  barTime: string;
  price: number;
};

export type StrategyRdLiquidityMarkerKind = "take" | "run" | "sweep";

export type StrategyRdLiquidityDrawMarker = {
  kind: StrategyRdLiquidityMarkerKind;
  barTime: string;
  price: number | null;
};

export function strategyRdIsLiquidityRunId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  if (key.includes("sweep")) return false;
  return key === "liq_run" || key === "prior_liq_run" || (key.includes("liq") && key.includes("run"));
}

export function strategyRdIsLiquiditySweepId(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  if (!key) return false;
  return (
    key === "liq_sweep" ||
    key === "prior_liq_sweep" ||
    key === "liquidity_sweep" ||
    (key.includes("liq") && key.includes("sweep")) ||
    (key.includes("sweep") && !key.includes("run"))
  );
}

function normalizeLiquidityMarkerKind(raw: unknown): StrategyRdLiquidityMarkerKind | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase();
  if (!key) return null;
  if (
    key === "take" ||
    key === "grab" ||
    key === "pool_take" ||
    key === "liq_take"
  ) {
    return "take";
  }
  if (
    key === "run" ||
    key === "liq_run" ||
    key === "run_confirm" ||
    key === "close_through"
  ) {
    return "run";
  }
  if (
    key === "sweep" ||
    key === "reclaim" ||
    key === "sweep_reclaim" ||
    key === "liq_sweep"
  ) {
    return "sweep";
  }
  return null;
}

/**
 * BE liquidity draw markers — Investing.com / ICT:
 * Take = pool grab; Sweep = take then reverse (reclaim); Run = take then continue.
 * Prefer `meta.markers[]` (Take + Sweep|Run). Never label a trap breakout as Run.
 */
export function strategyRdLiquidityDrawMarkers(
  meta: Record<string, unknown> | null | undefined,
  conceptId: string | null | undefined
): StrategyRdLiquidityDrawMarker[] {
  if (!meta) return [];
  const isRun = strategyRdIsLiquidityRunId(conceptId);
  const isSweep = strategyRdIsLiquiditySweepId(conceptId);
  if (!isRun && !isSweep) return [];

  type PartialMarker = {
    kind: StrategyRdLiquidityMarkerKind;
    barTime: string;
    price: number | null;
  };
  const fromMarkers: PartialMarker[] = [];
  const markersRaw = meta.markers;
  if (Array.isArray(markersRaw)) {
    for (const row of markersRaw) {
      const rec = asRecord(row);
      if (!rec) continue;
      const kind =
        normalizeLiquidityMarkerKind(rec.kind) ??
        normalizeLiquidityMarkerKind(rec.label) ??
        normalizeLiquidityMarkerKind(rec.type) ??
        normalizeLiquidityMarkerKind(rec.name);
      const barTime =
        asTime(rec.bar_time) ?? asTime(rec.time) ?? asTime(rec.timestamp);
      if (!kind || !barTime) continue;
      fromMarkers.push({
        kind,
        barTime,
        price: finiteNum(rec.price) ?? finiteNum(rec.level),
      });
    }
  }

  const poolPrice =
    finiteNum(meta.pool_level) ??
    finiteNum(asRecord(meta.pool_line)?.price) ??
    finiteNum(meta.take_price) ??
    finiteNum(meta.swing_high) ??
    finiteNum(meta.swing_low) ??
    finiteNum(meta.level) ??
    finiteNum(meta.price);

  const firstOf = (kind: StrategyRdLiquidityMarkerKind): PartialMarker | null =>
    fromMarkers.find((m) => m.kind === kind) ?? null;

  const markersChoseOutcome = fromMarkers.some(
    (m) => m.kind === "sweep" || m.kind === "run"
  );
  // Sweep beats Run when both appear — a trap breakout is never a Run.
  const outcome: "sweep" | "run" | null = markersChoseOutcome
    ? fromMarkers.some((m) => m.kind === "sweep")
      ? "sweep"
      : "run"
    : isSweep
      ? "sweep"
      : isRun
        ? "run"
        : null;
  if (!outcome) return [];

  const takeHit = firstOf("take");
  const takeTime = takeHit?.barTime ?? asTime(meta.take_bar_time);
  const takePrice = takeHit?.price ?? finiteNum(meta.take_price) ?? poolPrice;

  let outcomeTime: string | null = null;
  let outcomePrice: number | null = null;
  if (outcome === "sweep") {
    const hit = firstOf("sweep");
    outcomeTime =
      hit?.barTime ??
      (markersChoseOutcome
        ? null
        : asTime(meta.sweep_bar_time) ?? asTime(meta.reclaim_bar_time));
    outcomePrice =
      hit?.price ?? finiteNum(meta.sweep_price) ?? finiteNum(meta.reclaim_price) ?? poolPrice;
  } else {
    const hit = firstOf("run");
    outcomeTime = hit?.barTime ?? (markersChoseOutcome ? null : asTime(meta.run_bar_time));
    outcomePrice = hit?.price ?? finiteNum(meta.run_price) ?? poolPrice;
  }

  const out: StrategyRdLiquidityDrawMarker[] = [];
  // Same-bar reclaim: Take may be omitted — still paint Take on the outcome bar.
  const resolvedTakeTime = takeTime ?? outcomeTime;
  if (resolvedTakeTime) {
    out.push({ kind: "take", barTime: resolvedTakeTime, price: takePrice });
  }
  if (outcomeTime) {
    out.push({
      kind: outcome,
      barTime: outcomeTime,
      price: outcomePrice ?? poolPrice,
    });
  }
  return out;
}

export function strategyRdLiquidityMarkerLabel(kind: StrategyRdLiquidityMarkerKind): string {
  if (kind === "take") return "Take";
  if (kind === "run") return "Run";
  return "Sweep";
}

/**
 * BE liq_sweep / liq_run pool geometry — fractal swing being tested.
 * Price: pool_level | pool_line.price | level. Anchor: pool_bar_time (not Take).
 */
export function strategyRdLiquidityPoolRail(
  meta: Record<string, unknown> | null | undefined
): {
  price: number;
  fromBarTime: string | null;
  poolSide: "high" | "low" | null;
  poolKind: "buyside" | "sellside" | null;
} | null {
  if (!meta) return null;
  const poolLine = asRecord(meta.pool_line);
  const price =
    finiteNum(meta.pool_level) ??
    finiteNum(poolLine?.price) ??
    finiteNum(meta.level) ??
    finiteNum(meta.swing_high) ??
    finiteNum(meta.swing_low);
  if (price == null) return null;

  const fromBarTime =
    asTime(meta.pool_bar_time) ??
    asTime(poolLine?.from_bar_time) ??
    asTime(poolLine?.bar_time) ??
    null;

  const sideRaw = String(
    meta.pool_side ?? poolLine?.side ?? meta.sweep_side ?? meta.run_side ?? ""
  )
    .trim()
    .toLowerCase();
  let poolSide: "high" | "low" | null = null;
  let poolKind: "buyside" | "sellside" | null = null;
  if (sideRaw === "high" || sideRaw === "buyside" || sideRaw === "buy") {
    poolSide = "high";
    poolKind = "buyside";
  } else if (sideRaw === "low" || sideRaw === "sellside" || sideRaw === "sell") {
    poolSide = "low";
    poolKind = "sellside";
  } else {
    const kindRaw = String(meta.pool_kind ?? poolLine?.kind ?? "")
      .trim()
      .toLowerCase();
    if (kindRaw === "buyside" || kindRaw === "buy") {
      poolSide = "high";
      poolKind = "buyside";
    } else if (kindRaw === "sellside" || kindRaw === "sell") {
      poolSide = "low";
      poolKind = "sellside";
    }
  }

  return { price, fromBarTime, poolSide, poolKind };
}

/**
 * Prior liquidity: the pivot high (short) or pivot low (long) taken out
 * in the bars leading into the setup.
 */
export function strategyRdLiquiditySweepFromCandles(
  candles: readonly StrategyRdConceptOhlcLike[] | null | undefined,
  setupBarTime: string | null | undefined,
  direction?: "long" | "short" | "neutral" | null
): SweepHit | null {
  const list = candles ?? [];
  const t = (setupBarTime || "").trim();
  if (!t || list.length < 5) return null;
  const i = indexOfBarTime(list, t);
  if (i < 3) return null;
  const start = Math.max(1, i - 16);
  const end = i - 1;
  const wantLow = direction === "long";
  let bestIdx = -1;
  let bestPrice = wantLow ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
  for (let j = start; j <= end; j += 1) {
    const row = list[j];
    if (!row) continue;
    const high = Number(row.high);
    const low = Number(row.low);
    const prevHigh = Number(list[j - 1]?.high);
    const nextHigh = Number(list[j + 1]?.high);
    const prevLow = Number(list[j - 1]?.low);
    const nextLow = Number(list[j + 1]?.low);
    if (wantLow) {
      if (!Number.isFinite(low)) continue;
      const pivot = (!Number.isFinite(prevLow) || low <= prevLow) && (!Number.isFinite(nextLow) || low <= nextLow);
      if (pivot && low < bestPrice) {
        bestIdx = j;
        bestPrice = low;
      }
    } else {
      if (!Number.isFinite(high)) continue;
      const pivot =
        (!Number.isFinite(prevHigh) || high >= prevHigh) &&
        (!Number.isFinite(nextHigh) || high >= nextHigh);
      if (pivot && high > bestPrice) {
        bestIdx = j;
        bestPrice = high;
      }
    }
  }
  if (bestIdx < 0) return null;
  const barTime = candleTime(list[bestIdx]!);
  if (!barTime || !Number.isFinite(bestPrice)) return null;
  return { barTime, price: bestPrice };
}

type FvgGap = {
  high: number;
  low: number;
  fromBarTime: string;
  toBarTime: string;
};

function fvgGapAt(
  candles: readonly StrategyRdConceptOhlcLike[],
  left: number,
  right: number,
  prefer: "bullish" | "bearish" | null
): FvgGap | null {
  const c1 = candles[left];
  const c3 = candles[right];
  if (!c1 || !c3) return null;
  const h1 = Number(c1.high);
  const l1 = Number(c1.low);
  const h3 = Number(c3.high);
  const l3 = Number(c3.low);
  if (![h1, l1, h3, l3].every(Number.isFinite)) return null;
  const fromBarTime = candleTime(c1);
  const toBarTime = candleTime(c3);
  if (!fromBarTime || !toBarTime) return null;
  const bullish = h1 < l3;
  const bearish = l1 > h3;
  const bull: FvgGap | null = bullish
    ? { high: l3, low: h1, fromBarTime, toBarTime }
    : null;
  const bear: FvgGap | null = bearish
    ? { high: l1, low: h3, fromBarTime, toBarTime }
    : null;
  if (prefer === "bearish") return bear ?? bull;
  if (prefer === "bullish") return bull ?? bear;
  if (bull && bear) {
    return bull.high - bull.low >= bear.high - bear.low ? bull : bear;
  }
  return bull ?? bear;
}

/**
 * Classic 3-candle FVG: empty price between candle 1 and candle 3.
 * Setup bar_time may be the middle impulse or the confirming third bar.
 */
export function strategyRdFvgZoneFromCandles(
  candles: readonly StrategyRdConceptOhlcLike[] | null | undefined,
  setupBarTime: string | null | undefined,
  direction?: "long" | "short" | "neutral" | null
): FvgGap | null {
  const list = candles ?? [];
  const t = (setupBarTime || "").trim();
  if (!t || list.length < 3) return null;
  const i = indexOfBarTime(list, t);
  if (i < 0) return null;
  const prefer =
    direction === "short" ? "bearish" : direction === "long" ? "bullish" : null;
  const candidates = [
    i >= 2 ? fvgGapAt(list, i - 2, i, prefer) : null,
    i >= 1 && i + 1 < list.length ? fvgGapAt(list, i - 1, i + 1, prefer) : null,
  ].filter((g): g is FvgGap => g != null);
  if (!candidates.length) return null;
  return candidates.reduce((best, next) =>
    next.high - next.low > best.high - best.low ? next : best
  );
}

/**
 * Bucket chart-TF candles into source-TF OHLC bars (e.g. 5m → 15m) so we can
 * compute a real HTF FVG, then draw that gap on the finer chart.
 */
export function resampleConceptCandlesToTf(
  candles: readonly StrategyRdConceptOhlcLike[] | null | undefined,
  sourceCandleType: string | null | undefined
): StrategyRdConceptOhlcLike[] {
  const list = candles ?? [];
  const bucketMs = executionCandleTypeDurationMs(sourceCandleType);
  if (!list.length || !bucketMs) return [];
  type Acc = {
    open: number;
    high: number;
    low: number;
    close: number;
    timestamp: string;
  };
  const buckets = new Map<number, Acc>();
  const order: number[] = [];
  for (const c of list) {
    const t = candleTime(c);
    if (!t) continue;
    const ms = Date.parse(t.replace(/\+00:00$/, "Z"));
    if (!Number.isFinite(ms)) continue;
    const key = Math.floor(ms / bucketMs) * bucketMs;
    const o = Number(c.open);
    const h = Number(c.high);
    const l = Number(c.low);
    const cl = Number(c.close);
    if (![h, l].every(Number.isFinite)) continue;
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, {
        open: Number.isFinite(o) ? o : h,
        high: h,
        low: l,
        close: Number.isFinite(cl) ? cl : l,
        timestamp: new Date(key).toISOString(),
      });
      order.push(key);
    } else {
      existing.high = Math.max(existing.high, h);
      existing.low = Math.min(existing.low, l);
      if (Number.isFinite(cl)) existing.close = cl;
    }
  }
  return order.map((key) => {
    const row = buckets.get(key)!;
    return {
      timestamp: row.timestamp,
      open: row.open,
      high: row.high,
      low: row.low,
      close: row.close,
    };
  });
}

/** Resolve an FVG on `sourceTf` (resampling chart candles when needed). */
export function strategyRdFvgZoneOnSourceTf(opts: {
  candles?: readonly StrategyRdConceptOhlcLike[] | null;
  chartCandleType?: string | null;
  sourceCandleType?: string | null;
  barTime?: string | null;
  direction?: "long" | "short" | "neutral" | null;
}): FvgGap | null {
  const chartTf = (opts.chartCandleType || "").trim();
  const sourceTf = (opts.sourceCandleType || chartTf).trim();
  const barTime = (opts.barTime || "").trim();
  if (!barTime) return null;
  const series =
    !chartTf || !sourceTf || executionCandleTypesMatch(sourceTf, chartTf)
      ? opts.candles ?? []
      : resampleConceptCandlesToTf(opts.candles, sourceTf);
  if (!series.length) return null;

  // Snap wall-clock bar_time to the containing source-TF bucket open.
  const bucketMs = executionCandleTypeDurationMs(sourceTf);
  let anchor = barTime;
  if (bucketMs) {
    const ms = Date.parse(barTime.replace(/\+00:00$/, "Z"));
    if (Number.isFinite(ms)) {
      anchor = new Date(Math.floor(ms / bucketMs) * bucketMs).toISOString();
    }
  }
  return strategyRdFvgZoneFromCandles(series, anchor, opts.direction);
}

/**
 * BE returns the example match location; FE maps selected setup + confluences
 * to labeled drawings around that location (not full-chart Trade Review overlays).
 * Confluence boxes/levels come only from `meta.confluences.{id}` (projected onto
 * the chart TF). Missing geometry → marker only — never invent a box.
 */
export function strategyRdConceptDrawings(opts: {
  match: StrategyRdMatch | null | undefined;
  setupId?: string | null;
  confluences?: readonly (string | StrategyRdConceptConfluenceRef)[] | null;
  candles?: readonly StrategyRdConceptOhlcLike[] | null;
  /** Chart series TF — used to project coarser confluence windows. */
  chartCandleType?: string | null;
  /** Fallback when a confluence slot omits candle_type. */
  setupCandleType?: string | null;
  /** Preset sessions — killzone shade windows. */
  sessions?: readonly StrategyRdSessionOption[] | null;
}): StrategyRdConceptDrawing[] {
  const match = opts.match;
  if (!match) return [];

  const setupId = (opts.setupId || match.setup_id || match.pattern_id || "").trim();
  const setupIdLower = setupId.toLowerCase();
  const isLiqSetup =
    setupIdLower === "liq_sweep" ||
    setupIdLower === "liq_run" ||
    (setupIdLower.includes("liq") &&
      (setupIdLower.includes("sweep") || setupIdLower.includes("run")));
  const setupBar = match.path?.setup_bar_time?.trim() || match.bar_time;
  const chartTf = (opts.chartCandleType || match.candle_type || "").trim();
  const setupTf = (opts.setupCandleType || match.candle_type || chartTf).trim();
  const out: StrategyRdConceptDrawing[] = [];

  const isWaveCSetup =
    strategyRdWaveCIsCandlePatternId(setupId) ||
    strategyRdWaveCIsChartPatternId(setupId) ||
    strategyRdWaveCIsBandId(setupId) ||
    strategyRdWaveCIsOscillatorId(setupId);
  if (isWaveCSetup && match.meta) {
    const waveC = strategyRdWaveCDrawingsFromMeta({
      prefix: `setup-zone:${setupId || "setup"}`,
      conceptId: setupId,
      meta: {
        ...(match.meta as Record<string, unknown>),
        bar_time:
          (match.meta as Record<string, unknown>).bar_time ?? match.bar_time,
      },
      candles: opts.candles,
      sessions: opts.sessions,
    });
    for (const d of waveC) {
      out.push(
        withProjectedTf(
          {
            id: d.id,
            label: d.label,
            kind: d.kind,
            barTime: d.barTime,
            fromBarTime: d.fromBarTime,
            toBarTime: d.toBarTime,
            high: d.high,
            low: d.low,
            price: d.price,
            fromPrice: d.fromPrice,
            toPrice: d.toPrice,
            lineExtend: d.lineExtend,
            arrowRight: d.arrowRight,
            showLabel: d.showLabel,
            paneId: d.paneId,
          },
          setupTf,
          chartTf
        )
      );
    }
  }

  // pct_break / stage breakout may carry vol expand / RVOL on match.meta — annotate volume pane.
  if (match.meta && !strategyRdWaveCIsVolumeId(setupId)) {
    const matchMeta = match.meta as Record<string, unknown>;
    const volLoc = parseStrategyRdWaveCLocation(matchMeta);
    const isPctBreak =
      setupIdLower === "pct_break_bar" || setupIdLower.includes("pct_break");
    const isStageBreakout =
      strategyRdIsStageSetupId(setupId) &&
      String(matchMeta.signal_mode ?? "").trim().toLowerCase() !== "regime";
    if (
      (isPctBreak || isStageBreakout) &&
      !volLoc.softPass &&
      strategyRdWaveCHasVolumeEvidence(volLoc)
    ) {
      const volMeta = {
        ...matchMeta,
        bar_time: volLoc.barTime || match.bar_time,
      };
      const waveCVol = strategyRdWaveCDrawingsFromMeta({
        prefix: `setup:volume:${setupId || "setup"}`,
        conceptId: volLoc.rvol != null ? "rvol_strong" : "vol_expansion",
        meta: volMeta,
        candles: opts.candles,
        sessions: opts.sessions,
      });
      for (const d of waveCVol) {
        if (d.paneId !== "volume") continue;
        out.push(
          withProjectedTf(
            {
              id: d.id,
              label: d.label,
              kind: d.kind,
              barTime: d.barTime,
              fromBarTime: d.fromBarTime,
              toBarTime: d.toBarTime,
              high: d.high,
              low: d.low,
              price: d.price,
              fromPrice: d.fromPrice,
              toPrice: d.toPrice,
              lineExtend: d.lineExtend,
              showLabel: d.showLabel,
              paneId: d.paneId,
            },
            setupTf,
            chartTf
          )
        );
      }
    }
  }

  // Wave C candle/chart setups already drew — skip classic zone path for those.
  const skipClassicSetupZone = isWaveCSetup && out.some((d) => d.id.startsWith("setup-zone:"));

  const metaZoneRaw = extractStrategyRdMetaZone(match.meta);
  // Liquidity setups draw fractal swings — never promote PDH/PWH calendar rails as the setup.
  const metaZone =
    isLiqSetup && metaZoneRaw && (metaZoneRaw.label === "PDH/PDL" || metaZoneRaw.label === "PDH")
      ? null
      : metaZoneRaw;
  // Prefer full ORB range (orb_high/orb_low) over trimmed path level_high/level_low.
  // FVG setup boxes come from BE meta only — never invent from chart OHLC.
  const fvgMeta = isFvgSetupId(setupId) && metaZone?.label === "FVG" ? metaZone : null;
  const orbZone = metaZone?.label === "ORB" ? metaZone : null;
  const breakerZone = strategyRdIsBreakerSetupId(setupId)
    ? extractStrategyRdBreakerSetupZone(match.meta)
    : null;
  const orderBlockZone = strategyRdIsOrderBlockSetupId(setupId)
    ? extractStrategyRdOrderBlockSetupZone(match.meta)
    : null;
  const coilSpan =
    setupId.includes("coil_range_contract")
      ? extractStrategyRdCoilSetupSpan(match.meta)
      : null;
  const stageGeom = strategyRdIsStageSetupId(setupId)
    ? extractStrategyRdStageSetupGeometry({
        ...((match.meta as Record<string, unknown> | null | undefined) || {}),
        bar_time:
          (match.meta as Record<string, unknown> | null | undefined)?.bar_time ??
          match.bar_time,
      })
    : null;
  const stageSegments = strategyRdIsStageSetupId(setupId)
    ? extractStrategyRdStageSegments(match.meta)
    : [];
  const pathZone = strategyRdPathSetupZone(match.path);
  // Drop path levels that don't touch the setup candle — they float as empty boxes.
  const setupCandleIdx = setupBar ? indexOfBarTime(opts.candles ?? [], setupBar) : -1;
  const setupCandle =
    setupCandleIdx >= 0 ? (opts.candles ?? [])[setupCandleIdx] ?? null : null;
  const pathZoneOk =
    pathZone &&
    (!setupCandle ||
      (finiteNum(setupCandle.high) != null &&
        finiteNum(setupCandle.low) != null &&
        pathZone.low <= Number(setupCandle.high) &&
        pathZone.high >= Number(setupCandle.low)));
  // OB/Breaker: never use path/FVG levels as the setup box (displacement gap ≠ origin body).
  let zone:
    | {
        high: number;
        low: number;
        label: string;
        fromBarTime?: string;
        toBarTime?: string;
      }
    | null = null;
  if (!skipClassicSetupZone) {
  if (fvgMeta) {
    // Always the origin gap prices (IFVG = same FVG box, flipped role on invalidate).
    const isIfvg =
      setupIdLower === "ifvg" ||
      setupIdLower.includes("inverse_fvg") ||
      setupIdLower.includes("inv_fvg");
    // FVG: from_bar_time → to_bar_time (triplet). IFVG: birth → invalidate (= signal).
    const originBar = isIfvg
      ? asTime(match.meta?.origin_bar_time) ??
        asTime(match.meta?.from_bar_time) ??
        asTime(match.meta?.fvg_bar_time) ??
        null
      : asTime(match.meta?.from_bar_time) ??
        asTime(match.meta?.fvg_bar_time) ??
        asTime(match.meta?.origin_bar_time) ??
        null;
    const metaEnd = isIfvg
      ? asTime(match.meta?.to_bar_time) ?? setupBar ?? asTime(match.meta?.bar_time) ?? null
      : asTime(match.meta?.to_bar_time) ?? setupBar ?? null;
    // Retest-then-close / break path: FE-extend the band to the story (no BE change).
    // IFVG right edge stays invalidate — do not stretch past the flip signal.
    const endBar = isIfvg
      ? metaEnd
      : latestBarTime(
          metaEnd,
          match.path?.retest_bar_time,
          match.path?.events?.find((e) => e.kind === "retest")?.bar_time,
          match.path?.break_bar_time,
          match.path?.events?.find((e) => e.kind === "break")?.bar_time,
          strategyRdPathReadyEnter(match.path)?.barTime
        );
    zone = {
      high: fvgMeta.high,
      low: fvgMeta.low,
      label: isIfvg ? "IFVG" : "FVG",
      fromBarTime: originBar ?? undefined,
      toBarTime: endBar ?? undefined,
    };
  } else if (orderBlockZone) {
    // Horizontal body band from origin candle forward (impulse / entry) — not a one-bar box.
    const entryBar = strategyRdPathReadyEnter(match.path)?.barTime ?? null;
    const fromBar =
      orderBlockZone.fromBarTime ||
      asTime(match.meta?.ob_bar_time) ||
      setupBar ||
      null;
    const toBar =
      latestBarTime(
        orderBlockZone.toBarTime,
        entryBar,
        asTime(match.meta?.impulse_bar_time),
        asTime(match.meta?.mss_bar_time),
        match.path?.retest_bar_time,
        match.path?.events?.find((e) => e.kind === "retest")?.bar_time
      ) ||
      orderBlockZone.toBarTime ||
      fromBar;
    zone = {
      high: orderBlockZone.high,
      low: orderBlockZone.low,
      label: "Order block",
      fromBarTime: fromBar ?? undefined,
      toBarTime: toBar ?? undefined,
    };
  } else if (breakerZone) {
    zone = {
      high: breakerZone.high,
      low: breakerZone.low,
      label: "Breaker",
      fromBarTime: breakerZone.fromBarTime ?? undefined,
      toBarTime: breakerZone.toBarTime ?? undefined,
    };
  } else if (coilSpan) {
    zone = {
      high: coilSpan.high,
      low: coilSpan.low,
      label: "Coil",
      fromBarTime: coilSpan.fromBarTime,
      toBarTime: coilSpan.toBarTime,
    };
  } else if (orbZone) {
    zone = orbZone;
  } else if (
    metaZone &&
    (metaZone.label === "Order block" || metaZone.label === "Breaker")
  ) {
    zone = metaZone;
  } else if (
    !isLiqSetup &&
    !strategyRdIsOrderBlockSetupId(setupId) &&
    !strategyRdIsBreakerSetupId(setupId) &&
    !strategyRdIsLevelPairSetupId(setupId) &&
    !strategyRdIsStageSetupId(setupId) &&
    !strategyRdWaveCIsDonchianId(setupId)
  ) {
    // Liquidity setups draw pool_level + Take/Sweep|Run markers — never a level_high/low box.
    // Stage draws its own segments / base box — never a degenerate meta.level zone.
    // Donchian draws upper_line/lower_line rails — never level_high/low on Enter.
    zone = (pathZoneOk ? pathZone : null) ?? metaZone;
  }

  // High/low setups (DH/DL, PDH/PDL, PWH/PWL, PMH/PML, Swing H/L):
  // horizontal rails — never a vertical box between extremes.
  // After Level filter BE often sends a single meta.level + break_side.
  if (strategyRdIsLevelPairSetupId(setupId) && setupBar) {
    const meta = match.meta;
    const isDeveloping =
      setupIdLower.includes("developing") || setupIdLower === "developing_day_hl";
    const isWeek =
      setupIdLower === "pwh_pwl" ||
      (setupIdLower.includes("pwh") && setupIdLower.includes("pwl"));
    const isMonth =
      setupIdLower === "pmh_pml" ||
      (setupIdLower.includes("pmh") && setupIdLower.includes("pml"));
    const isSwing =
      setupIdLower === "swing_hl" || setupIdLower === "swing_high_low";

    const tags = isDeveloping
      ? { high: "DH", low: "DL" }
      : isWeek
        ? { high: "PWH", low: "PWL" }
        : isMonth
          ? { high: "PMH", low: "PML" }
          : isSwing
            ? { high: "Swing H", low: "Swing L" }
            : { high: "PDH", low: "PDL" };

    const breakSideRaw = String(meta?.break_side ?? meta?.level_side ?? "")
      .trim()
      .toLowerCase();
    const breakIsHigh =
      breakSideRaw === "high" ||
      breakSideRaw === "pdh" ||
      breakSideRaw === "pwh" ||
      breakSideRaw === "pmh" ||
      breakSideRaw === "swing_high" ||
      breakSideRaw.endsWith("_high");
    const breakIsLow =
      breakSideRaw === "low" ||
      breakSideRaw === "pdl" ||
      breakSideRaw === "pwl" ||
      breakSideRaw === "pml" ||
      breakSideRaw === "swing_low" ||
      breakSideRaw.endsWith("_low");

    const filteredLevel = finiteNum(meta?.level);
    const swingAnchor =
      isSwing
        ? asTime(meta?.pool_bar_time) ??
          asTime(asRecord(meta?.pool_line)?.from_bar_time) ??
          asTime(meta?.swing_bar_time) ??
          asTime(meta?.from_bar_time)
        : null;

    type Rail = {
      price: number;
      label: string;
      side: "high" | "low";
      fromBarTime: string | null;
    };
    const rails: Rail[] = [];

    // Developing day: always paint both DH/DL from day_* (active break may null one level_*).
    // Other level pairs: after Level filter BE may send a single meta.level + break_side.
    if (
      !isDeveloping &&
      filteredLevel != null &&
      (breakIsHigh || breakIsLow)
    ) {
      rails.push({
        price: filteredLevel,
        label: breakIsHigh ? tags.high : tags.low,
        side: breakIsHigh ? "high" : "low",
        fromBarTime: swingAnchor,
      });
      // Prior day / week / month: draw both prior levels (BE sends pdh + pdl even when
      // only one side broke). Swing H/L keeps just the broken pool.
      if (!isSwing) {
        const otherPrice = breakIsHigh
          ? isWeek
            ? finiteNum(meta?.pwl)
            : isMonth
              ? finiteNum(meta?.pml)
              : finiteNum(meta?.pdl)
          : isWeek
            ? finiteNum(meta?.pwh)
            : isMonth
              ? finiteNum(meta?.pmh)
              : finiteNum(meta?.pdh);
        if (otherPrice != null && otherPrice !== filteredLevel) {
          rails.push({
            price: otherPrice,
            label: breakIsHigh ? tags.low : tags.high,
            side: breakIsHigh ? "low" : "high",
            fromBarTime: null,
          });
        }
      }
    } else {
      const high = isDeveloping
        ? finiteNum(meta?.day_high) ??
          finiteNum(meta?.developing_high) ??
          finiteNum(meta?.level_high) ??
          finiteNum(meta?.level) ??
          finiteNum(meta?.high) ??
          pathZone?.high ??
          null
        : isWeek
          ? finiteNum(meta?.pwh) ??
            finiteNum(meta?.prior_week_high) ??
            finiteNum(meta?.level_high) ??
            finiteNum(meta?.high) ??
            pathZone?.high ??
            null
          : isMonth
            ? finiteNum(meta?.pmh) ??
              finiteNum(meta?.prior_month_high) ??
              finiteNum(meta?.level_high) ??
              finiteNum(meta?.high) ??
              pathZone?.high ??
              null
            : isSwing
              ? finiteNum(meta?.swing_high) ??
                finiteNum(meta?.level_high) ??
                finiteNum(meta?.high) ??
                pathZone?.high ??
                null
              : finiteNum(meta?.pdh) ??
                finiteNum(meta?.prior_day_high) ??
                finiteNum(meta?.level_high) ??
                finiteNum(meta?.high) ??
                pathZone?.high ??
                null;
      const low = isDeveloping
        ? finiteNum(meta?.day_low) ??
          finiteNum(meta?.developing_low) ??
          finiteNum(meta?.level_low) ??
          finiteNum(meta?.low) ??
          pathZone?.low ??
          null
        : isWeek
          ? finiteNum(meta?.pwl) ??
            finiteNum(meta?.prior_week_low) ??
            finiteNum(meta?.level_low) ??
            finiteNum(meta?.low) ??
            pathZone?.low ??
            null
          : isMonth
            ? finiteNum(meta?.pml) ??
              finiteNum(meta?.prior_month_low) ??
              finiteNum(meta?.level_low) ??
              finiteNum(meta?.low) ??
              pathZone?.low ??
              null
            : isSwing
              ? finiteNum(meta?.swing_low) ??
                finiteNum(meta?.level_low) ??
                finiteNum(meta?.low) ??
                pathZone?.low ??
                null
              : finiteNum(meta?.pdl) ??
                finiteNum(meta?.prior_day_low) ??
                finiteNum(meta?.level_low) ??
                finiteNum(meta?.low) ??
                pathZone?.low ??
                null;
      if (high != null) {
        rails.push({ price: high, label: tags.high, side: "high", fromBarTime: swingAnchor });
      }
      if (low != null) {
        rails.push({ price: low, label: tags.low, side: "low", fromBarTime: swingAnchor });
      }
      // Filtered single level without break_side — still draw meta.level once.
      if (rails.length === 0 && filteredLevel != null) {
        rails.push({
          price: filteredLevel,
          label: strategyRdConceptChartLabel(setupId) || tags.high,
          side: "high",
          fromBarTime: swingAnchor,
        });
      }
    }

    // Unspanned levels → chart-wide priceLines (see emit). Swing may span from pivot.
    const enterBar = strategyRdPathReadyEnter(match.path)?.barTime ?? setupBar;
    for (const rail of rails) {
      const fromBar = rail.fromBarTime;
      const spanned = Boolean(fromBar);
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}:${rail.side}`,
            label: rail.label,
            kind: "level",
            barTime: setupBar,
            fromBarTime: spanned ? fromBar : null,
            toBarTime: spanned ? enterBar : null,
            high: null,
            low: null,
            price: rail.price,
          },
          setupTf,
          chartTf
        )
      );
    }
    if (rails.length === 0) {
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}`,
            label: strategyRdConceptChartLabel(setupId) || "Setup",
            kind: "marker",
            barTime: setupBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: null,
          },
          setupTf,
          chartTf
        )
      );
    }
  } else if (zone && zone.high > zone.low) {
    const zoneWithSpan = zone as {
      high: number;
      low: number;
      label: string;
      fromBarTime?: string;
      toBarTime?: string;
    };
    const overlapsSetup =
      !setupCandle ||
      (finiteNum(setupCandle.high) != null &&
        finiteNum(setupCandle.low) != null &&
        zone.low <= Number(setupCandle.high) &&
        zone.high >= Number(setupCandle.low));
    // FVG / IFVG / OB / Breaker: BE prices are authoritative — always draw the band
    // even when the signal candle has already closed through (no OHLC overlap).
    const alwaysDrawZone =
      isFvgSetupId(setupId) ||
      strategyRdIsOrderBlockSetupId(setupId) ||
      strategyRdIsBreakerSetupId(setupId) ||
      Boolean(coilSpan) ||
      zone.label === "FVG" ||
      zone.label === "IFVG" ||
      zone.label === "Order block" ||
      zone.label === "Breaker" ||
      zone.label === "Coil";
    // Anchor to the signal bar when BE omits span — avoids a 4-bar floating ghost box.
    const fromBar =
      zoneWithSpan.fromBarTime ?? coilSpan?.fromBarTime ?? setupBar ?? null;
    const toBar =
      zoneWithSpan.toBarTime ?? coilSpan?.toBarTime ?? setupBar ?? null;
    if (overlapsSetup || alwaysDrawZone) {
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}`,
            // Always the concept tag (OB / BB / FVG) — never initials of "Setup range" → "Sr".
            label: strategyRdConceptChartLabel(setupId) || "Setup",
            kind: "zone",
            // Breaker: signal bar = failure bar (when the OB breaks).
            barTime: coilSpan?.toBarTime ?? breakerZone?.failBarTime ?? setupBar,
            fromBarTime: fromBar,
            toBarTime: toBar,
            high: zone.high,
            low: zone.low,
            price: (zone.high + zone.low) / 2,
          },
          setupTf,
          chartTf
        )
      );
    } else if (setupBar) {
      // Geometry doesn't meet the signal candle — pill only, no floating box.
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}`,
            label: strategyRdConceptChartLabel(setupId) || "Setup",
            kind: "marker",
            barTime: coilSpan?.toBarTime ?? breakerZone?.failBarTime ?? setupBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: null,
          },
          setupTf,
          chartTf
        )
      );
    }
  } else if (
    setupBar &&
    (isFvgSetupId(setupId) ||
      strategyRdIsBreakerSetupId(setupId) ||
      strategyRdIsOrderBlockSetupId(setupId) ||
      setupId.includes("order_block") ||
      /(^|_)ob(_|$)/.test(setupId.toLowerCase()) ||
      setupId.includes("session_first") ||
      setupId.includes("opening_range"))
  ) {
    // Zone setup without drawable geometry — pill on the setup bar only.
    out.push(
      withProjectedTf(
        {
          id: `setup-zone:${setupId || "setup"}`,
          label: strategyRdConceptChartLabel(setupId) || "Setup",
          kind: "marker",
          barTime: setupBar,
          fromBarTime: null,
          toBarTime: null,
          high: null,
          low: null,
          price: null,
        },
        setupTf,
        chartTf
      )
    );
  }

  // Stage analysis — branch on meta.signal_mode (do not invent geometry from OHLC).
  // regime: SMA + stage_segments + stage_flip + Enter
  // breakout: SMA + stage_segments + base box + break level + Base/Break markers + Enter
  // BE trims stage_segments (≤4 trailing, no pre-base / flicker history) — draw as-is.
  if (stageGeom || stageSegments.length > 0) {
    const stageBar =
      stageGeom?.barTime || setupBar || stageGeom?.toBarTime || null;
    const signalMode = stageGeom?.signalMode ?? "breakout";

    // §3b Stage bands — both modes when BE sends stage_segments[].
    for (const seg of stageSegments) {
      const hasFill =
        seg.high != null && seg.low != null && seg.high > seg.low;
      if (hasFill) {
        out.push(
          withProjectedTf(
            {
              id: `setup-stage-seg:${setupId || "stage"}:${seg.stage ?? "x"}:${seg.fromBarTime}`,
              label: seg.label,
              kind: "zone",
              barTime: seg.toBarTime,
              fromBarTime: seg.fromBarTime,
              toBarTime: seg.toBarTime,
              high: seg.high,
              low: seg.low,
              price: (seg.high! + seg.low!) / 2,
            },
            setupTf,
            chartTf
          )
        );
      } else {
        out.push(
          withProjectedTf(
            {
              id: `setup-stage-seg:${setupId || "stage"}:${seg.stage ?? "x"}:${seg.fromBarTime}`,
              label: seg.label,
              kind: "marker",
              barTime: seg.toBarTime || seg.fromBarTime,
              fromBarTime: seg.fromBarTime,
              toBarTime: seg.toBarTime,
              high: null,
              low: null,
              price: null,
            },
            setupTf,
            chartTf
          )
        );
      }
    }

    const stageMarkers = strategyRdStageDrawMarkers(
      match.meta as Record<string, unknown> | null | undefined,
      { signalMode }
    );
    const hasBaseMarker = stageMarkers.some((m) => m.kind === "base");

    if (signalMode === "breakout" && stageGeom) {
      // Breakout base box (BE times + pattern_high/low only).
      if (
        stageGeom.fromBarTime &&
        stageGeom.toBarTime &&
        stageGeom.boxHigh != null &&
        stageGeom.boxLow != null &&
        stageGeom.boxHigh > stageGeom.boxLow
      ) {
        out.push(
          withProjectedTf(
            {
              id: `setup-zone:${setupId || "stage"}:base`,
              label: "Base",
              kind: "zone",
              barTime: stageGeom.toBarTime,
              fromBarTime: stageGeom.fromBarTime,
              toBarTime: stageGeom.toBarTime,
              high: stageGeom.boxHigh,
              low: stageGeom.boxLow,
              price: (stageGeom.boxHigh + stageGeom.boxLow) / 2,
              // Marker already says "Base" — avoid stacked duplicate pills.
              showLabel: !hasBaseMarker,
            },
            setupTf,
            chartTf
          )
        );
      }
      if (stageGeom.level != null) {
        const levelFrom = stageGeom.fromBarTime || null;
        const levelTo = levelFrom ? stageBar || stageGeom.toBarTime || null : null;
        out.push(
          withProjectedTf(
            {
              id: `setup-level:${setupId || "stage"}`,
              label: "Break level",
              kind: "level",
              barTime: stageBar,
              fromBarTime: levelFrom,
              toBarTime: levelTo,
              high: null,
              low: null,
              price: stageGeom.level,
            },
            setupTf,
            chartTf
          )
        );
      }
    }

    for (const m of stageMarkers) {
      out.push(
        withProjectedTf(
          {
            id: `setup-marker:${setupId || "stage"}:${m.kind}:${m.barTime}`,
            label: m.label,
            kind: "marker",
            barTime: m.barTime,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: m.price ?? stageGeom?.level ?? null,
          },
          setupTf,
          chartTf
        )
      );
    }

    // SMA line is enough — pill stacks on Enter/Breakout at the signal bar.
    if (stageGeom?.maValue != null && stageBar) {
      const len =
        stageGeom.maLength != null && Number.isFinite(stageGeom.maLength)
          ? Math.round(stageGeom.maLength)
          : null;
      out.push(
        withProjectedTf(
          {
            id: `setup-ma:${setupId || "stage"}`,
            label: len != null ? `SMA ${len}` : "SMA",
            kind: "marker",
            barTime: stageBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: stageGeom.maValue,
            showLabel: false,
          },
          setupTf,
          chartTf
        )
      );
    }

    declutterStageConceptLabels(out, {
      signalBarTime: stageBar,
      enterBarTime: strategyRdPathReadyEnter(match.path)?.barTime ?? stageBar,
    });
  }

  // Liquidity setup (sweep/run): pool rail + BE Take + Sweep|Run from meta.markers[].
  // Always emit event markers — do not let a prior setup-zone pill steal this path.
  if (isLiqSetup && setupBar) {
    const meta = match.meta as Record<string, unknown> | null | undefined;
    const pool = strategyRdLiquidityPoolRail(meta ?? null);
    const poolLevel = pool?.price ?? null;
    const eventMarkers = strategyRdLiquidityDrawMarkers(meta ?? null, setupId);
    const hasEvents = eventMarkers.length > 0;

    // Drop any earlier setup-zone* for this id (e.g. accidental Level range box).
    for (let i = out.length - 1; i >= 0; i -= 1) {
      if (out[i]!.id.startsWith(`setup-zone:${setupId || "setup"}`)) {
        out.splice(i, 1);
      }
    }

    if (poolLevel != null) {
      const outcomeHit = eventMarkers.find((m) => m.kind === "sweep" || m.kind === "run");
      // Anchor = fractal swing bar (pool_bar_time) — not Take / Sweep.
      const fromBar =
        pool?.fromBarTime ??
        asTime(meta?.take_bar_time) ??
        eventMarkers.find((m) => m.kind === "take")?.barTime ??
        setupBar;
      const toBar =
        latestBarTime(
          outcomeHit?.barTime,
          asTime(meta?.sweep_bar_time),
          asTime(meta?.run_bar_time),
          strategyRdPathReadyEnter(match.path)?.barTime,
          setupBar
        ) ?? fromBar;
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}`,
            label: "Pool",
            kind: "level",
            barTime: fromBar,
            // Fractal swing → Enter (or Sweep|Run): cyan pool rail.
            fromBarTime: fromBar,
            toBarTime: toBar,
            high: null,
            low: null,
            price: poolLevel,
            showLabel: !hasEvents,
          },
          setupTf,
          chartTf
        )
      );
    } else if (!hasEvents) {
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}`,
            label: strategyRdConceptChartLabel(setupId) || "Setup",
            kind: "marker",
            barTime: setupBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: null,
            showLabel: true,
          },
          setupTf,
          chartTf
        )
      );
    }

    for (const m of eventMarkers) {
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId || "setup"}:${m.kind}`,
            label: strategyRdLiquidityMarkerLabel(m.kind),
            kind: "marker",
            barTime: m.barTime,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: m.price ?? poolLevel,
            showLabel: true,
          },
          setupTf,
          chartTf
        )
      );
    }
  }
  // Order block: the match is the market-structure shift that confirmed the block — mark it
  // at the broken level so the box (block candle → MSS) reads left to right.
  if (orderBlockZone && match.meta) {
    const mssBar = asTime(match.meta.mss_bar_time);
    const mssLevel = finiteNum(match.meta.mss_level);
    if (mssBar && mssLevel != null) {
      const mssKind = String(match.meta.mss_kind ?? "").trim().toLowerCase();
      out.push(
        withProjectedTf(
          {
            id: `setup-marker:${setupId || "ob"}:mss`,
            label: mssKind === "bos" ? "BOS" : mssKind === "choch" ? "CHoCH" : "MSS",
            kind: "marker",
            barTime: mssBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: mssLevel,
          },
          setupTf,
          chartTf
        )
      );
    }
  }

  // MA crossover / pullback: marker at the cross or touch on the MA (the lines are lane-B overlays).
  if (setupBar && match.meta) {
    const meta = match.meta as Record<string, unknown>;
    const maKind = String(meta.ma_kind ?? "").trim().toUpperCase() || "MA";
    const fastValue = finiteNum(meta.fast_value);
    const isCross = setupIdLower.endsWith("_cross") && (setupIdLower.startsWith("sma") || setupIdLower.startsWith("ema"));
    if (isCross && fastValue != null) {
      const fast = finiteNum(meta.fast);
      const slow = finiteNum(meta.slow);
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId}:cross`,
            label: fast != null && slow != null ? `${maKind} ${fast}/${slow} cross` : `${maKind} cross`,
            kind: "marker",
            barTime: setupBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: fastValue,
          },
          setupTf,
          chartTf
        )
      );
    }
    const maValue = finiteNum(meta.ma_value);
    if (setupIdLower.startsWith("pullback_to_ma") && maValue != null) {
      const len = finiteNum(meta.ma_length);
      out.push(
        withProjectedTf(
          {
            id: `setup-zone:${setupId}:touch`,
            label: len != null ? `${maKind} ${Math.round(len)} touch` : `${maKind} touch`,
            kind: "marker",
            barTime: setupBar,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: maValue,
          },
          setupTf,
          chartTf
        )
      );
    }
  }
  } // end !skipClassicSetupZone

  // Everything drawn so far is the setup's; each confluence tags its own below.
  for (const d of out) d.group = d.group ?? "setup";
  for (const entry of strategyRdConfluenceEvidenceEntries(match.meta, opts.confluences)) {
    const groupStart = out.length;
    try {
    // `id` = concept (drives how to draw); `drawKey` = slot key (keeps repeat chips apart).
    const id = entry.conceptId;
    const drawKey = entry.slotKey.replace(/#/g, "__");
    const ref = entry.ref;
    const evidence = entry.evidence;
    const softPass =
      evidence && typeof evidence.soft_pass === "string"
        ? evidence.soft_pass.trim()
        : "";
    // Soft-skipped evidence — no geometry. RS confluences without benchmark data say so on
    // the setup bar (otherwise the chip silently draws nothing and looks broken).
    if (softPass) {
      if (softPass === "benchmark_unavailable" && strategyRdWaveCIsRsId(entry.conceptId)) {
        const at = asTime(evidence?.bar_time) ?? setupBar;
        if (at) {
          out.push(
            withProjectedTf(
              {
                id: `confluence:${drawKey}:unchecked`,
                label: `${strategyRdConceptChartLabel(entry.conceptId) || "RS"}: no benchmark data (unchecked)`,
                kind: "marker",
                barTime: at,
                fromBarTime: null,
                toBarTime: null,
                high: null,
                low: null,
                price: null,
              },
              (ref?.candle_type || setupTf || chartTf).trim(),
              chartTf
            )
          );
        }
      }
      continue;
    }
    if (evidence && evidence.armed === false) continue;

    const loc = confluenceLocationFromRow(evidence);
    const armBar =
      asTime(evidence?.arm_bar_time) ??
      asTime(evidence?.armed_bar_time) ??
      loc.barTime;
    const entryBar = strategyRdPathReadyEnter(match.path)?.barTime ?? null;
    if (locationArmsAfterEntry(armBar, entryBar)) continue;

    const sourceTf = (loc.candleType || ref?.candle_type || setupTf || chartTf).trim();
    const sameTf = !chartTf || !sourceTf || executionCandleTypesMatch(sourceTf, chartTf);
    // Same-TF MAs paint as continuous lane-B lines — skip one-bar MA markers.
    // Oscillator panes still get BE hit markers below.
    const maLane = strategyRdConceptUsesChartIndicator(id);
    if (maLane && sameTf && !strategyRdWaveCIsOscillatorId(id) && !strategyRdWaveCIsBandId(id)) {
      continue;
    }
    if (strategyRdIsStageSetupId(id)) {
      pushStageConfluenceDrawings(out, {
        id: drawKey,
        evidence,
        sourceTf,
        chartTf,
        fallbackBar: armBar || loc.barTime || setupBar,
      });
      continue;
    }

    if (id === "clear_of_opposing_sr") {
      pushClearOfOpposingSrDrawings(out, {
        id: drawKey,
        evidence,
        sourceTf,
        chartTf,
        fallbackBar: armBar || loc.barTime || setupBar,
      });
      continue;
    }

    if (strategyRdConceptIsChartWideFilter(id) && !maLane) continue;

    // Own momentum / RS vs benchmark: the measured move (from_bar_time, from_close) →
    // (bar_time, to_close) with the numbers as a badge ("Gold +0.40% vs SPY −0.10% = +0.50%").
    if ((id === "own_momentum" || id === "rs_vs_benchmark") && evidence) {
      const fromT = asTime(evidence.from_bar_time);
      const toT = asTime(evidence.bar_time) ?? loc.barTime;
      const fromClose = finiteNum(evidence.from_close);
      const toClose = finiteNum(evidence.to_close);
      if (fromT && toT && fromClose != null && toClose != null) {
        const pct = (n: number | null) =>
          n == null ? null : `${n > 0 ? "+" : n < 0 ? "\u2212" : ""}${Math.abs(n).toFixed(2)}%`;
        let badge: string;
        if (id === "own_momentum") {
          const ret = pct(finiteNum(evidence.return_pct));
          const bars = finiteNum(evidence.lookback);
          badge = `Mom ${ret ?? ""}${bars != null ? ` / ${Math.round(bars)} bars` : ""}`.trim();
        } else {
          const inst = pct(finiteNum(evidence.inst_ret_pct));
          const bench = pct(finiteNum(evidence.bench_ret_pct));
          const rs = pct(finiteNum(evidence.rs_pct));
          // Readable benchmark (`benchmark_label`, e.g. "EUR vs peers (GBP, …) in USD");
          // never the raw code (FX_PEERS).
          const label =
            typeof evidence.benchmark_label === "string" ? evidence.benchmark_label.trim() : "";
          const symbol =
            typeof evidence.benchmark_symbol === "string" ? evidence.benchmark_symbol.trim() : "";
          const benchName =
            label || (symbol && symbol.toUpperCase() !== "FX_PEERS" ? symbol : "benchmark");
          const selfName = (match.symbol || "").trim() || "This";
          badge =
            inst && bench && rs
              ? / vs /i.test(benchName)
                ? // The label already names both sides ("EUR vs peers … in USD").
                  `${benchName}: ${inst} vs ${bench} = ${rs}`
                : `${selfName} ${inst} vs ${benchName} ${bench} = ${rs}`
              : rs
                ? `RS ${rs}`
                : "RS";
        }
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}`,
              label: badge,
              kind: "ray",
              barTime: toT,
              fromBarTime: fromT,
              toBarTime: toT,
              high: null,
              low: null,
              fromPrice: fromClose,
              toPrice: toClose,
              price: toClose,
              lineExtend: "none",
            },
            sourceTf,
            chartTf
          )
        );
        continue;
      }
    }

    // ATR extension: the MA and ±ATR bands come from the BE series over price, so the
    // evidence is one badge on price at the setup bar ("Ext 1.11×ATR from SMA 50") —
    // never an oscillator-pane marker (it has no pane and floated at 0).
    if (id === "atr_ext_from_ma" && evidence) {
      const ext = finiteNum(evidence.atr_ext);
      const maValue = finiteNum(evidence.ma_value) ?? finiteNum(evidence.sma_value);
      const maLen = finiteNum(evidence.ma_length);
      const at = asTime(evidence.bar_time) ?? loc.barTime ?? setupBar;
      if (at) {
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}`,
              label:
                ext != null
                  ? `Ext ${ext.toFixed(2)}\u00d7ATR${maLen != null ? ` from SMA ${Math.round(maLen)}` : ""}`
                  : "ATR ext",
              kind: "marker",
              barTime: at,
              fromBarTime: null,
              toBarTime: null,
              high: null,
              low: null,
              price: maValue,
            },
            sourceTf,
            chartTf
          )
        );
      }
      continue;
    }

    // Not overextended legs (swing mode): number each leg at its start (`leg_points[]`)
    // and badge the count vs the limit on the setup bar.
    if (id === "not_overextended_legs" && evidence && Array.isArray(evidence.leg_points)) {
      const legs = finiteNum(evidence.legs);
      const maxLegs = finiteNum(evidence.max_legs);
      let drewLeg = false;
      evidence.leg_points.forEach((raw, i) => {
        const row = asRecord(raw);
        const at = asTime(row?.bar_time);
        const price = finiteNum(row?.price);
        if (!at || price == null) return;
        const n = finiteNum(row?.leg) ?? i + 1;
        drewLeg = true;
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}:leg:${i}`,
              label: String(Math.round(n)),
              kind: "marker",
              barTime: at,
              fromBarTime: null,
              toBarTime: null,
              high: null,
              low: null,
              price,
              showLabel: true,
            },
            sourceTf,
            chartTf
          )
        );
      });
      const at = asTime(evidence.bar_time) ?? loc.barTime ?? setupBar;
      if (at && (legs != null || drewLeg)) {
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}`,
              label:
                legs != null && maxLegs != null
                  ? `Legs ${Math.round(legs)}/${Math.round(maxLegs)}`
                  : `Legs ${legs != null ? Math.round(legs) : ""}`.trim(),
              kind: "marker",
              barTime: at,
              fromBarTime: null,
              toBarTime: null,
              high: null,
              low: null,
              price: null,
            },
            sourceTf,
            chartTf
          )
        );
      }
      continue;
    }

    // Mansfield RS: the line itself is a pane series (indicator_series); mark the setup bar.
    if (id.includes("mansfield") && evidence) {
      const value = finiteNum(evidence.mansfield_rs);
      const at = asTime(evidence.bar_time) ?? loc.barTime ?? setupBar;
      if (value != null && at) {
        out.push({
          id: `confluence:${drawKey}:osc`,
          label: `MRS ${value >= 0 ? "+" : "\u2212"}${Math.abs(value).toFixed(2)}`,
          kind: "marker",
          barTime: at,
          fromBarTime: null,
          toBarTime: null,
          high: null,
          low: null,
          price: value,
          paneId: "tessera-panes",
        });
        continue;
      }
    }

    const waveCConfluence =
      strategyRdWaveCIsCandlePatternId(id) ||
      strategyRdWaveCIsChartPatternId(id) ||
      strategyRdWaveCIsBandId(id) ||
      strategyRdWaveCIsOscillatorId(id) ||
      strategyRdWaveCIsVolumeId(id) ||
      strategyRdWaveCIsRsId(id) ||
      strategyRdWaveCIsKillzoneId(id);
    if (waveCConfluence && evidence) {
      const waveC = strategyRdWaveCDrawingsFromMeta({
        prefix: `confluence:${drawKey}`,
        conceptId: id,
        meta: evidence,
        candles: opts.candles,
        sessions: opts.sessions,
      });
      if (waveC.length) {
        for (const d of waveC) {
          out.push(
            withProjectedTf(
              {
                id: d.id,
                label: d.label,
                kind: d.kind,
                barTime: d.barTime,
                fromBarTime: d.fromBarTime,
                toBarTime: d.toBarTime,
                high: d.high,
                low: d.low,
                price: d.price,
                fromPrice: d.fromPrice,
                toPrice: d.toPrice,
                lineExtend: d.lineExtend,
                arrowRight: d.arrowRight,
                showLabel: d.showLabel,
                paneId: d.paneId,
              },
              sourceTf,
              chartTf
            )
          );
        }
        continue;
      }
    }

    // Trendline: the textbook line through two pivots (`anchor_start_*` → `anchor_end_*`),
    // extended to the entry; every touch in `touch_points[]` is marked. Never OHLC-fitted.
    if (id === "trendline" && evidence) {
      const pivotsRaw = Array.isArray(evidence.pivots) ? evidence.pivots : [];
      const pivot0 = asRecord(pivotsRaw[0]);
      const pivot1 = asRecord(pivotsRaw[1]);
      const aTime = asTime(evidence.anchor_start_time) ?? asTime(pivot0?.bar_time);
      const aPrice = finiteNum(evidence.anchor_start_price) ?? finiteNum(pivot0?.price);
      const bTime = asTime(evidence.anchor_end_time) ?? asTime(pivot1?.bar_time);
      const bPrice = finiteNum(evidence.anchor_end_price) ?? finiteNum(pivot1?.price);
      if (aTime && bTime && aPrice != null && bPrice != null && aTime !== bTime) {
        // Extend to the entry (else the setup) along the same line. Slope per bar on the
        // chart's own candles when the times are on it; else per unit of time.
        const target = latestBarTime(bTime, entryBar ?? setupBar) ?? bTime;
        const list = opts.candles ?? [];
        const ia = indexOfBarTime(list, aTime);
        const ib = indexOfBarTime(list, bTime);
        const it = indexOfBarTime(list, target);
        let targetPrice: number = bPrice;
        if (sameTf && ia >= 0 && ib > ia && it >= 0) {
          targetPrice = aPrice + ((bPrice - aPrice) * (it - ia)) / (ib - ia);
        } else {
          const ma = barTimeMs(aTime);
          const mb = barTimeMs(bTime);
          const mt = barTimeMs(target);
          if (ma != null && mb != null && mt != null && mb !== ma) {
            targetPrice = aPrice + ((bPrice - aPrice) * (mt - ma)) / (mb - ma);
          }
        }
        const lineKind =
          typeof evidence.line_kind === "string" ? evidence.line_kind.trim().toLowerCase() : "";
        const rayLabel =
          lineKind === "support" ? "Support" : lineKind === "resistance" ? "Resist" : "TL";
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}`,
              label: rayLabel,
              kind: "ray",
              barTime: bTime,
              fromBarTime: aTime,
              toBarTime: target,
              high: null,
              low: null,
              fromPrice: aPrice,
              toPrice: targetPrice,
              price: (aPrice + targetPrice) / 2,
              lineExtend: "none",
            },
            sourceTf,
            chartTf
          )
        );
        const touchesRaw = Array.isArray(evidence.touch_points)
          ? evidence.touch_points
          : pivotsRaw;
        let touchIndex = 0;
        for (const raw of touchesRaw) {
          const row = asRecord(raw);
          const t = asTime(row?.bar_time);
          const price = finiteNum(row?.price);
          if (!t || price == null) continue;
          out.push(
            withProjectedTf(
              {
                id: `confluence:${drawKey}:touch:${touchIndex}`,
                label: "Touch",
                kind: "marker",
                barTime: t,
                fromBarTime: null,
                toBarTime: null,
                high: null,
                low: null,
                price,
                showLabel: false,
              },
              sourceTf,
              chartTf
            )
          );
          touchIndex += 1;
        }
        continue;
      }
    }

    const hasZone = loc.high != null && loc.low != null && loc.high > loc.low;
    const hasLevel = loc.price != null;
    // Cross-TF zones: keep BE prices + projected times. Never invent from chart OHLC.
    // If evidence has no geometry, marker only (do not fill from primary series).
    const kind =
      hasZone || hasLevel ? kindFromConfluenceLocation(id, loc) : ("marker" as const);

    // Wrong-TF box without BE geometry → skip inventing; marker still ok.
    if (!sameTf && !hasZone && !hasLevel && !loc.barTime) continue;

    const bosClass =
      id === "bos" && evidence && typeof evidence.bos_class === "string"
        ? evidence.bos_class.trim().toLowerCase()
        : "";
    const priorLabel =
      id === "near_prior_sr" && evidence
        ? String(
            evidence.high_label ??
              evidence.low_label ??
              evidence.label ??
              evidence.scope_label ??
              ""
          ).trim()
        : "";
    const swingKind =
      id === "near_swing" && evidence && typeof evidence.swing_kind === "string"
        ? evidence.swing_kind.trim().toLowerCase()
        : "";
    // Value badges (reference: badge with the value, no geometry beyond a dot at the MA).
    const adrPct = id === "adr_pct" ? finiteNum(evidence?.adr_pct) : null;
    const pctFromMa = id === "pct_from_ma" ? finiteNum(evidence?.pct_from_ma) : null;
    const pctMaLen = finiteNum(evidence?.ma_length);
    const pctMaKind = String(evidence?.ma_kind ?? "").trim().toUpperCase() || "MA";
    const valueBadge =
      adrPct != null
        ? `ADR ${adrPct.toFixed(2)}%`
        : pctFromMa != null
          ? `${pctMaKind}${pctMaLen != null ? ` ${Math.round(pctMaLen)}` : ""} ${pctFromMa >= 0 ? "+" : ""}${pctFromMa.toFixed(2)}%`
          : null;
    const label =
      valueBadge ??
      (id === "htf_bias"
        ? strategyRdHtfBiasChartLabel(
            typeof evidence?.scope === "string" ? evidence.scope : null,
            typeof evidence?.bias === "string" ? evidence.bias : null
          )
        : bosClass === "major"
          ? "BOS·M"
          : priorLabel
            ? priorLabel
            : swingKind === "high"
              ? "Swing H"
              : swingKind === "low"
                ? "Swing L"
                : strategyRdConceptChartLabel(id));

    // Liquidity / near_swing: fractal swing level (not PDH/PWH calendar rails).
    const liqSwing =
      id === "near_swing" ||
      id.includes("liq") ||
      id.includes("sweep") ||
      id.includes("run")
        ? finiteNum(evidence?.swing_high) != null ||
          finiteNum(evidence?.swing_low) != null ||
          finiteNum(evidence?.level) != null
        : false;
    const swingHigh = liqSwing ? finiteNum(evidence?.swing_high) : null;
    const swingLow = liqSwing ? finiteNum(evidence?.swing_low) : null;
    // Prior sweep / run: the pool is the side that was taken (`sweep_side` / `run_side`),
    // never a box between both swings.
    const poolSide = String(evidence?.sweep_side ?? evidence?.run_side ?? "")
      .trim()
      .toLowerCase();
    const poolLevel =
      (liqSwing || id.includes("liq") ? finiteNum(evidence?.pool_level) : null) ??
      (poolSide === "high" ? swingHigh : poolSide === "low" ? swingLow : null);
    const swingLevel =
      poolLevel ??
      (swingHigh != null && swingLow != null && swingHigh !== swingLow
        ? null
        : swingHigh ??
          swingLow ??
          (id === "near_swing" ? finiteNum(evidence?.level) : null));

    // near_prior_sr: single calendar extreme from evidence.level / price.
    const priorLevel =
      id === "near_prior_sr"
        ? finiteNum(evidence?.level) ??
          finiteNum(evidence?.price) ??
          finiteNum(evidence?.high) ??
          finiteNum(evidence?.low)
        : null;

    const levelPrice = swingLevel ?? priorLevel;
    // Formation stays the left edge and the prices. Stretch the band through the fill
    // when entry is later than to_bar_time (same idea as setup FVG).
    const zoneToBarTime =
      hasZone && levelPrice == null
        ? latestBarTime(loc.toBarTime, entryBar) ?? loc.toBarTime
        : null;

    // BOS / CHoCH: line at `level` from the broken swing (`swing_bar_time`) to the break (`bar_time`).
    const isStructure = id === "bos" || id === "choch";
    const swingBarTime = isStructure ? asTime(evidence?.swing_bar_time) : null;
    const structureSpan =
      swingBarTime && loc.barTime && loc.price != null && !hasZone
        ? { from: swingBarTime, to: loc.barTime }
        : null;
    // Prior sweep / run: pool line from the swing (`pool_bar_time`) to the take.
    const poolFrom =
      id.includes("liq") ? asTime(evidence?.pool_bar_time) : null;
    const poolTo =
      id.includes("liq")
        ? asTime(evidence?.take_bar_time) ?? loc.barTime
        : null;
    const lineSpan =
      structureSpan ??
      (poolFrom && poolTo && poolFrom !== poolTo ? { from: poolFrom, to: poolTo } : null);

    const liqEvents =
      id.includes("liq") || id.includes("sweep") || id.includes("run")
        ? strategyRdLiquidityDrawMarkers(evidence, id)
        : [];
    const hideLiqLevelLabel = liqEvents.length > 0;

    out.push(
      withProjectedTf(
        {
          id: `confluence:${drawKey}`,
          label,
          // % from MA: a dot at the MA on the setup bar, not a chart-wide line.
          kind: levelPrice != null ? "level" : pctFromMa != null ? "marker" : kind,
          barTime: armBar || loc.barTime || setupBar,
          fromBarTime: lineSpan
            ? lineSpan.from
            : hasZone && levelPrice == null
              ? loc.fromBarTime
              : null,
          toBarTime: lineSpan ? lineSpan.to : zoneToBarTime,
          high: hasZone && levelPrice == null ? loc.high : null,
          low: hasZone && levelPrice == null ? loc.low : null,
          price:
            levelPrice != null
              ? levelPrice
              : hasZone
                ? (loc.high! + loc.low!) / 2
                : hasLevel
                  ? loc.price
                  : null,
          showLabel: hideLiqLevelLabel ? false : undefined,
        },
        sourceTf,
        chartTf
      )
    );

    for (const m of liqEvents) {
      out.push(
        withProjectedTf(
          {
            id: `confluence:${drawKey}:${m.kind}`,
            label: strategyRdLiquidityMarkerLabel(m.kind),
            kind: "marker",
            barTime: m.barTime,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: m.price ?? levelPrice,
            showLabel: true,
          },
          sourceTf,
          chartTf
        )
      );
    }

    // Reject (BOS / CHoCH / prior sweep / run): the opposing event failed — mark the failing
    // close at `fail_bar_time` / `fail_level` (older BOS evidence: `confirm_bar_time` at level).
    const rejectable = isStructure || id.includes("liq");
    const failBar = rejectable ? asTime(evidence?.fail_bar_time) : null;
    const failLevel = rejectable ? finiteNum(evidence?.fail_level) : null;
    const structureConfirm =
      failBar ?? (isStructure ? asTime(evidence?.confirm_bar_time) : null);
    const rejectPrice = failLevel ?? (isStructure ? loc.price : null);
    if (structureConfirm && rejectPrice != null && (failBar || structureConfirm !== loc.barTime)) {
      out.push(
        withProjectedTf(
          {
            id: `confluence:${drawKey}:reject`,
            label: "Fail",
            kind: "marker",
            barTime: structureConfirm,
            fromBarTime: null,
            toBarTime: null,
            high: null,
            low: null,
            price: rejectPrice,
            showLabel: true,
          },
          sourceTf,
          chartTf
        )
      );
    }

    // Optional inducement level when BOS major swept it.
    if (id === "bos" && evidence && evidence.inducement_swept === true) {
      const indLevel = finiteNum(evidence.inducement_level);
      const indBar =
        asTime(evidence.inducement_bar_time) ?? armBar ?? loc.barTime ?? setupBar;
      if (indLevel != null && indBar) {
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}:inducement`,
              label: "Ind",
              kind: "level",
              barTime: indBar,
              fromBarTime: null,
              toBarTime: null,
              high: null,
              low: null,
              price: indLevel,
            },
            sourceTf,
            chartTf
          )
        );
      }
    }

    // Breaker origin failure — optional marker at fail_bar_time.
    if (id === "near_breaker" && evidence) {
      const failBar =
        asTime(evidence.fail_bar_time) ?? asTime(evidence.origin_ob_bar_time);
      if (failBar) {
        out.push(
          withProjectedTf(
            {
              id: `confluence:${drawKey}:fail`,
              label: "Fail",
              kind: "marker",
              barTime: failBar,
              fromBarTime: null,
              toBarTime: null,
              high: null,
              low: null,
              price: hasZone ? (loc.high! + loc.low!) / 2 : loc.price,
              showLabel: true,
            },
            sourceTf,
            chartTf
          )
        );
      }
    }
    } finally {
      for (let i = groupStart; i < out.length; i += 1) {
        out[i]!.group = out[i]!.group ?? `conf-${entry.chipIndex}`;
      }
    }
  }
  const entryStart = out.length;

  const path = match.path;
  // One Enter per hit — only when path is ready (never on path_horizon_exceeded).
  // Wave C / signal_bar_pills must not stamp Enter; encode direction in the id
  // so the chart palette paints long green / short red.
  const readyEnter = strategyRdPathReadyEnter(path);
  const alreadyHasEnter = out.some(
    (d) =>
      d.label.trim().toLowerCase() === "enter" ||
      /:enter(?:$|:)/i.test(d.id)
  );
  if (readyEnter && !alreadyHasEnter) {
    const dir = strategyRdMatchDisplayDirection(match);
    const dirKey = dir === "long" || dir === "short" ? dir : "x";
    out.push(
      withProjectedTf(
        {
          id: `setup-zone:${setupId || "setup"}:enter:${dirKey}`,
          label: "Enter",
          kind: "marker",
          barTime: readyEnter.barTime,
          fromBarTime: null,
          toBarTime: null,
          high: null,
          low: null,
          price: readyEnter.price,
          showLabel: true,
        },
        setupTf,
        chartTf
      )
    );
    // The fill price is the position tool's own entry line (blue, to the price axis) — no second,
    // direction-coloured line here.
  }

  // Enter pill + entry line: the Entry group.
  for (let i = entryStart; i < out.length; i += 1) out[i]!.group = out[i]!.group ?? "entry";
  return out;
}

/** Unique drawings for every preview example (ids include bar_time so they do not clobber). */
export function strategyRdConceptDrawingsForMatches(opts: {
  matches: readonly StrategyRdMatch[] | null | undefined;
  setupId?: string | null;
  confluences?: readonly (string | StrategyRdConceptConfluenceRef)[] | null;
  candles?: readonly StrategyRdConceptOhlcLike[] | null;
  chartCandleType?: string | null;
  setupCandleType?: string | null;
  sessions?: readonly StrategyRdSessionOption[] | null;
}): StrategyRdConceptDrawing[] {
  const matches = opts.matches ?? [];
  if (!matches.length) return [];
  const out: StrategyRdConceptDrawing[] = [];
  for (const match of matches) {
    const suffix = (match.bar_time || "").trim() || String(out.length);
    for (const drawing of strategyRdConceptDrawings({
      match,
      setupId: opts.setupId,
      confluences: opts.confluences,
      candles: opts.candles,
      chartCandleType: opts.chartCandleType,
      setupCandleType: opts.setupCandleType,
      sessions: opts.sessions,
    })) {
      out.push({
        ...drawing,
        id: `${drawing.id}:${suffix}`,
        // Preserve explicit hide (e.g. trendline pivot dots); default on for overview.
        showLabel: drawing.showLabel ?? true,
      });
    }
  }
  return out;
}

/**
 * One continuous line per level: spanned `level` drawings with the same label and price
 * (e.g. the Clear-of-S/R PWH over its prior week + the S/R rail near the setup) merge into
 * a single segment from the earliest start to the latest end. Chart-wide (unspanned) levels,
 * zones and pane drawings are left alone.
 */
export function strategyRdMergeLevelSegments(
  drawings: readonly StrategyRdConceptDrawing[]
): StrategyRdConceptDrawing[] {
  const out: StrategyRdConceptDrawing[] = [];
  const byKey = new Map<string, number>();
  for (const d of drawings) {
    const fromMs = barTimeMs(d.fromBarTime);
    const toMs = barTimeMs(d.toBarTime);
    const mergeable =
      d.kind === "level" &&
      !d.paneId &&
      d.price != null &&
      Number.isFinite(d.price) &&
      fromMs != null &&
      toMs != null &&
      d.label.trim() !== "";
    if (!mergeable) {
      out.push(d);
      continue;
    }
    const key = `${d.label.trim().toLowerCase()}|${d.price!.toPrecision(10)}|${d.sourceCandleType ?? ""}`;
    const at = byKey.get(key);
    if (at == null) {
      byKey.set(key, out.length);
      out.push({ ...d });
      continue;
    }
    const kept = out[at]!;
    const keptFrom = barTimeMs(kept.fromBarTime)!;
    const keptTo = barTimeMs(kept.toBarTime)!;
    if (fromMs < keptFrom) kept.fromBarTime = d.fromBarTime;
    if (toMs > keptTo) kept.toBarTime = d.toBarTime;
    if (kept.showLabel === false && d.showLabel !== false) kept.showLabel = d.showLabel;
  }
  return out;
}
